import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeWhile } from 'rxjs/operators';
import { ApiErrorFactory } from '../../core/api-errors';
import { TemplateApiService } from '../../core/template';
import { Template, TemplateLookupMessage } from '../../core/template/template';

@Injectable()
export class ManageTemplatesService {
  private templates$$: BehaviorSubject<Template[]> = new BehaviorSubject([]);
  readonly templates$: Observable<Template[]> = this.templates$$.asObservable();

  private templatesCreating$$: BehaviorSubject<Template[]> = new BehaviorSubject([]);
  readonly templatesCreating$: Observable<Template[]> = this.templatesCreating$$.asObservable();

  private templateLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly templateLoading$: Observable<boolean> = this.templateLoading$$.asObservable();

  isDuplicating = false;

  constructor(
    private templateService: TemplateApiService,
    protected alertService: SnackbarService,
    protected apiErrorFactory: ApiErrorFactory,
    private translateService: TranslateService,
  ) {}

  lookupTemplates(partnerId: string, isCreating: boolean): Observable<Template[]> {
    const lookupMessage = new TemplateLookupMessage([partnerId], true, '', isCreating);
    return this.templateService.lookup(lookupMessage).pipe(
      filter((templateResponse) => !!templateResponse),
      map((templateResponse) => templateResponse.templates),
    );
  }

  loadTemplates(partnerId: string, isCreating = false): void {
    this.isDuplicating = isCreating || this.isDuplicating;
    timer(0, 5000)
      .pipe(
        switchMap(() => this.lookupTemplates(partnerId, isCreating)),
        distinctUntilChanged((prev, curr) => this.compare(prev, curr, isCreating, partnerId)),
        map((templateList: Template[]) => this.populateTemplates(templateList, isCreating)),
        takeWhile((templateList: Template[]) => isCreating && !!templateList && !!templateList.length),
      )
      .subscribe(
        (res) => res,
        (err) => this.handleError(err),
        () => {
          if (!isCreating) {
            this.templateLoading$$.next(false);
          }
          this.isDuplicating = !isCreating && this.isDuplicating;
        },
      );
  }

  populateTemplates(templateList: Template[], isCreating: boolean): Template[] {
    if (isCreating) {
      this.templatesCreating$$.next(templateList);
    } else {
      this.templates$$.next(templateList);
    }
    return templateList;
  }

  compare(
    oldTemplateList: Template[] = [],
    newTemplateList: Template[] = [],
    isCreating: boolean,
    partnerId: string,
  ): boolean {
    const oldTemplateIds = oldTemplateList.map((template: Template) => template.siteId);
    const newTemplateIds = newTemplateList.map((template: Template) => template.siteId);

    // making sure to load the list of templates from SiteModel if the job of at least one template
    // from SiteReservationModel is complete in a single timer period
    if (
      isCreating &&
      (newTemplateIds.length < oldTemplateIds.length ||
        (newTemplateIds.length >= oldTemplateIds.length &&
          !oldTemplateIds.every((value, index) => value === newTemplateIds[index])))
    ) {
      this.loadTemplates(partnerId);
    }
    return (
      isCreating &&
      newTemplateIds.length === oldTemplateIds.length &&
      newTemplateIds.every((value, index) => value === oldTemplateIds[index])
    );
  }

  // responsible for removing template from current observable, not actually deleting the template
  deleteTemplateFromList(siteId: string): void {
    this.templates$$.next(this.templates$$.getValue().filter((t) => t.siteId !== siteId));
  }

  handleError(errorResponse: HttpErrorResponse): void {
    const parsedError = this.apiErrorFactory.parseErrorResponse(errorResponse);
    this.displayError(parsedError.getUserMessage());
  }

  displayError(error: string): void {
    this.alertService.openErrorSnack(error, this.translateService.instant('ALERTS.GENERAL.DISMISS'));
  }
}
